/**
 * รอบจ่ายที่ไม่ตรงเดือนปฏิทิน — เช่น "26 ชน 25" (TN Riches)
 *
 * ระบบคิดเงินของ PayPilot ยึด "งวด = เดือนปฏิทิน" (ดู lib/period-range.ts) และยัง
 * เป็นแบบนั้นอยู่ — ไฟล์นี้ใช้กับ **การดูและการดึงรายงาน** เท่านั้น เพื่อให้ HR
 * กระทบยอดกับรอบจ่ายจริงของบริษัทได้ โดยไม่ต้องโหลด 2 เดือนมาต่อกันใน Excel
 *
 * ⭐ กับดักวันที่: ห้ามใช้ `new Date(y, m, d)` แล้ว `.toISOString()` — บนเครื่องที่
 * timezone นำหน้า UTC (ไทย +7) วันจะเพี้ยนย้อนไป 1 วัน (เหตุผลเต็มอยู่ใน
 * lib/period-range.ts) ที่นี่จึงใช้ `Date.UTC` ล้วน ไม่พึ่ง timezone ของ runtime
 */

/** จำนวนวันของเดือนนั้น — "2026-02" → 28 */
export function daysInMonth(period: string): number {
  const [y, m] = period.split('-').map(Number);
  return new Date(Date.UTC(y!, m!, 0)).getUTCDate();
}

/** เดือนก่อนหน้า — "2026-01" → "2025-12" */
export function prevPeriod(period: string): string {
  const [y, m] = period.split('-').map(Number);
  const d = new Date(Date.UTC(y!, m! - 2, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** บวกวัน แบบไม่พึ่ง timezone — ("2026-08-31", 1) → "2026-09-01" */
export function addDays(date: string, n: number): string {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, d! + n)).toISOString().slice(0, 10);
}

/**
 * ช่วงวันที่ของรอบจ่ายที่ปิดงวดวันที่ `cutoffDay` โดยให้ **วันปิดงวดอยู่ในเดือน `period`**
 *
 *   payCycleRange(25, '2026-09') → { from: '2026-08-26', to: '2026-09-25' }
 *   payCycleRange(31, '2026-09') → { from: '2026-09-01', to: '2026-09-30' }  (= เดือนปฏิทิน)
 *
 * วันปิดงวดที่เกินจำนวนวันของเดือนจะถูกหดลงมาที่วันสุดท้ายของเดือนนั้น (ก.พ. ไม่มีวันที่ 30)
 * ซึ่งทำให้รอบติดกันพอดีเสมอ — ไม่มีวันที่ตกหล่นและไม่มีวันที่ถูกนับซ้ำสองรอบ
 */
export function payCycleRange(cutoffDay: number, period: string): { from: string; to: string } {
  const cut = Math.min(Math.max(Math.trunc(cutoffDay), 1), 31);
  const to = `${period}-${String(Math.min(cut, daysInMonth(period))).padStart(2, '0')}`;
  const prev = prevPeriod(period);
  const prevCut = `${prev}-${String(Math.min(cut, daysInMonth(prev))).padStart(2, '0')}`;
  return { from: addDays(prevCut, 1), to };
}

/** รอบจ่ายนี้ตรงกับเดือนปฏิทินพอดีไหม (cutoff = วันสุดท้ายของเดือน) */
export function isCalendarMonthCycle(cutoffDay: number, period: string): boolean {
  const { from, to } = payCycleRange(cutoffDay, period);
  return from === `${period}-01` && to === `${period}-${String(daysInMonth(period)).padStart(2, '0')}`;
}

/** เดือนถัดไป — "2026-12" → "2027-01" */
export function nextPeriod(period: string): string {
  const [y, m] = period.split('-').map(Number);
  const d = new Date(Date.UTC(y!, m!, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/**
 * งวดของ "รอบจ่ายที่กำลังเดินอยู่" ณ วันที่ `todayIso` — ใช้ตั้งค่าเริ่มต้นให้ปุ่ม
 * "รอบจ่ายนี้" ในหน้ารายงาน
 *
 *   cutoff 25, วันนี้ 2026-09-10 → '2026-09'  (รอบ 26 ส.ค. – 25 ก.ย. ยังไม่ปิด)
 *   cutoff 25, วันนี้ 2026-09-26 → '2026-10'  (รอบ 26 ก.ย. ปิดไปแล้ว เข้ารอบใหม่)
 */
export function currentCyclePeriod(cutoffDay: number, todayIso: string): string {
  const period = todayIso.slice(0, 7);
  const { to } = payCycleRange(cutoffDay, period);
  return todayIso <= to ? period : nextPeriod(period);
}

/**
 * fb#158 — วันจ่ายเงินเดือน (tenants.payday_day) เป็นคำ
 * 31 = "ทุกวันสิ้นเดือน" (เดือนสั้นจ่ายวันสุดท้ายของเดือน — rd-export ตัดด้วย min(วัน, วันสุดท้าย) อยู่แล้ว)
 * ลูกค้าไม่อยากให้สลิปเขียน "ทุกวันที่ 31" เพราะบางเดือนไม่มีวันที่ 31
 */
export const PAYDAY_END_OF_MONTH = 31;
export function paydayText(day: number | null | undefined): string | null {
  const d = Number(day);
  if (!Number.isInteger(d) || d < 1 || d > 31) return null;
  return d === PAYDAY_END_OF_MONTH ? 'ทุกวันสิ้นเดือน' : `ทุกวันที่ ${d} ของเดือน`;
}
